import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Users, Dumbbell, CalendarDays, UserCircle, DollarSign, Library,
  Settings, LogOut, Bell, Menu, Route, Stethoscope, BadgeCheck, FileStack, ShieldCheck, Wallet, Compass, Star, MessageCircle,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const NAVS = {
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/hipnoterapeutas", label: "Hipnoterapeutas", icon: Stethoscope },
    { to: "/admin/planos", label: "Planos", icon: BadgeCheck },
    { to: "/admin/conteudos", label: "Conteúdos", icon: Library },
    { to: "/admin/usuarios", label: "Usuários", icon: Users },
    { to: "/admin/configuracoes", label: "Configurações", icon: Settings },
  ],
  therapist: [
    { to: "/painel", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/painel/clientes", label: "Clientes", icon: Users },
    { to: "/painel/exercicios", label: "Exercícios", icon: Dumbbell },
    { to: "/painel/agenda", label: "Agenda", icon: CalendarDays },
    { to: "/painel/pagamentos", label: "Pagamentos", icon: Wallet },
    { to: "/painel/conteudos", label: "Conteúdos", icon: Library },
    { to: "/painel/perfil", label: "Meu Perfil", icon: UserCircle },
    { to: "/painel/valores", label: "Meus Valores", icon: DollarSign },
    { to: "/painel/avaliacoes", label: "Avaliações", icon: Star },
  ],
  client: [
    { to: "/app", label: "Início", icon: LayoutDashboard, end: true },
    { to: "/app/jornada", label: "Minha Jornada", icon: Route },
    { to: "/app/exercicios", label: "Meus Exercícios", icon: Dumbbell },
    { to: "/app/conteudos", label: "Conteúdos", icon: Library },
    { to: "/app/consultas", label: "Minhas Consultas", icon: CalendarDays },
    { to: "/app/profissional", label: "Meu Profissional", icon: Stethoscope },
  ],
};

function NavItems({ items, onNavigate }) {
  return (
    <nav className="space-y-1 px-3">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onNavigate}
          data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? "bg-primary text-white"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`
          }
        >
          <it.icon className="w-[18px] h-[18px]" aria-hidden="true" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Notifications() {
  const [items, setItems] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch {}
  };
  useEffect(() => { load(); }, []);
  const unread = items.filter((i) => !i.read).length;
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <DropdownMenu onOpenChange={(o) => o && markAll()}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-destructive text-white text-[10px] flex items-center justify-center">
              {unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notificações</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="max-h-80">
          {items.length === 0 && (
            <div className="px-3 py-6 text-sm text-muted-foreground text-center">Nenhuma notificação</div>
          )}
          {items.map((n) => (
            <div key={n.id} className="px-3 py-2.5 border-b border-border last:border-0">
              <p className="text-sm">{n.message}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {new Date(n.created_at).toLocaleDateString("pt-BR")}
              </p>
            </div>
          ))}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = (() => {
    const base = NAVS[user?.role] || [];
    if (user?.role === "client" && user?.self_registered) {
      const copy = [...base];
      copy.splice(1, 0, { to: "/app/encontrar", label: "Encontrar Profissional", icon: Compass });
      return copy;
    }
    return base;
  })();
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const roleLabel = { admin: "Administrador", therapist: "Hipnoterapeuta", client: "Cliente" }[user?.role];

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card">
        <div className="h-16 flex items-center px-4 border-b border-border">
          <Brand />
        </div>
        <div className="flex-1 py-4 overflow-y-auto">
          <NavItems items={items} />
        </div>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 text-accent" />
            Dados protegidos (LGPD)
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 flex items-center justify-between px-4 border-b border-border bg-card/70 backdrop-blur-xl sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <div className="h-16 flex items-center px-4 border-b border-border"><Brand /></div>
                <div className="py-4"><NavItems items={items} /></div>
              </SheetContent>
            </Sheet>
            <span className="text-sm font-medium text-muted-foreground hidden sm:block">{roleLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="https://wa.me/5548988226137?text=Ol%C3%A1%2C%20preciso%20de%20suporte%20no%20Teraviva." target="_blank" rel="noreferrer" data-testid="support-whatsapp-btn" title="Falar com o suporte">
              <Button variant="outline" size="sm" className="rounded-full gap-1.5 hidden sm:inline-flex"><MessageCircle className="w-4 h-4 text-accent" />Suporte</Button>
              <Button variant="ghost" size="icon" className="sm:hidden"><MessageCircle className="w-5 h-5 text-accent" /></Button>
            </a>
            <Notifications />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-primary text-white text-sm">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
